import { useEffect, useState } from "react";
import {
  getOrgAnalytics,
  ApiError,
  type OrgAnalytics,
} from "../api";

/**
 * Org analytics dashboard — full-page view (not a drawer) per
 * Eric's call. Reached from the curator launchpad's Org row
 * "Analytics" button. Renders KPI tiles + a top-riders leaderboard,
 * with explicit "Coming soon" tiles for metrics that need new
 * schema (attendance, volunteer hours, donations, Member tenure).
 *
 * UX laws applied:
 *   - Hick's: KPI grid capped at the most-useful tiles (Events /
 *     RSVPs / Trails / Crew / Push Reach). "Coming soon" cards sit
 *     visually quieter so the eye lands on real numbers first.
 *   - Aesthetic-usability: consistent tile size + typography across
 *     the grid. Mobile collapses to 2-col then 1-col.
 *   - Peak-end: header carries the org name + a single hero number
 *     ("X events held"). End of view is the leaderboard — celebratory.
 *   - Doherty: single round-trip fetches everything in one shot.
 */
export function OrgAnalyticsView({
  orgId,
  onBack,
  backLabel = "Back",
}: {
  orgId: string;
  onBack: () => void;
  /** Label for the back button. Defaults to a neutral "Back" so the
   *  view works from any parent surface. Callers (AdminWorkspace,
   *  future CuratorPanel drill-in, etc.) pass their destination
   *  name so the user knows where they're returning to. */
  backLabel?: string;
}) {
  const [data, setData] = useState<OrgAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    getOrgAnalytics(orgId)
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Couldn't load analytics.");
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (error) {
    return (
      <div className="analytics-view">
        <button type="button" className="ghost small" onClick={onBack}>
          ← {backLabel}
        </button>
        <div className="error" style={{ marginTop: 12 }}>{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="analytics-view">
        <button type="button" className="ghost small" onClick={onBack}>
          ← {backLabel}
        </button>
        <div className="muted" style={{ marginTop: 12 }}>Loading analytics…</div>
      </div>
    );
  }

  const { organization, metrics, topRiders, trails } = data;
  const totalRsvps =
    metrics.rsvpTotals.going + metrics.rsvpTotals.maybe + metrics.rsvpTotals.notGoing;

  return (
    <div className="analytics-view">
      <button type="button" className="ghost small" onClick={onBack}>
        ← Back to curator
      </button>

      {/* Hero header — org logo + name + a single celebratory line.
          Logo sits left of the title so the brand reads first; same
          visual rhythm as the AdminWorkspace OrgCard header so the
          curator's mental model carries over. */}
      <header className="analytics-hero">
        <div className="analytics-hero-eyebrow muted">Organization analytics</div>
        <div className="analytics-hero-identity">
          <div className="analytics-hero-logo" aria-hidden="true">
            {organization.logoUrl ? (
              <img src={organization.logoUrl} alt="" />
            ) : (
              <span className="analytics-hero-logo-placeholder">🏔</span>
            )}
          </div>
          <h2 style={{ margin: 0 }}>{organization.name}</h2>
        </div>
        <p className="muted" style={{ margin: "8px 0 0" }}>
          {metrics.eventsCount === 0
            ? "No events yet — once you host one, the numbers will fill in."
            : `${metrics.eventsCount} event${metrics.eventsCount === 1 ? "" : "s"} run across ${metrics.trailsCount} trail${metrics.trailsCount === 1 ? "" : "s"}.`}
        </p>
      </header>

      {/* Primary KPI grid — what we can actually compute. */}
      <div className="kpi-grid">
        <KpiTile label="Events" value={metrics.eventsCount} hint={
          metrics.eventsCancelled > 0
            ? `${metrics.eventsCancelled} cancelled`
            : undefined
        } />
        <KpiTile label="RSVPs" value={totalRsvps} hint={
          `${metrics.rsvpTotals.going} going · ${metrics.rsvpTotals.maybe} maybe`
        } />
        <KpiTile label="Trails" value={metrics.trailsCount} />
        <KpiTile label="Crew" value={metrics.crewCount} hint={
          metrics.crewTenureAvgDays != null
            ? `${humanDays(metrics.crewTenureAvgDays)} avg tenure`
            : undefined
        } />
        <KpiTile label="Push reach" value={metrics.pushReach} hint="devices subscribed" />
      </div>

      {/* "Coming soon" — metrics that need new schema. Visually
          quieter so the eye doesn't read them as zeros. */}
      <div className="kpi-grid kpi-grid-pending">
        <PendingKpi
          label="Attendance"
          hint="Needs an after-event check-in flow to track who actually showed up."
        />
        <PendingKpi
          label="Volunteer hours"
          hint="Computed from attendance × event duration once check-in ships."
        />
        <PendingKpi
          label="Donations"
          hint="Pending the donation log (Stripe or manual entry)."
        />
        <PendingKpi
          label="Member tenure"
          hint="Pending the org-level Member CRM concept (separate from Crew)."
        />
      </div>

      {/* Leaderboard — top riders by RSVP volume. */}
      <section className="card" style={{ marginTop: 20 }}>
        <h3 style={{ margin: 0 }}>Top riders</h3>
        <p className="muted" style={{ marginTop: 4, marginBottom: 12 }}>
          Most RSVPs across this org's events. Attendance ranking comes once
          check-in ships.
        </p>
        {topRiders.length === 0 ? (
          <div className="empty">No RSVPs yet.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Rider</th>
                  <th className="num">Going</th>
                  <th className="num hide-sm">Maybe</th>
                </tr>
              </thead>
              <tbody>
                {topRiders.map((r) => (
                  <tr key={r.userId}>
                    <td>{r.email}</td>
                    <td className="num">{r.goingCount}</td>
                    <td className="num hide-sm">{r.maybeCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Trails — supporting context. Helps the curator see the
          per-org scope when the numbers above feel abstract. */}
      <section className="card" style={{ marginTop: 16 }}>
        <h3 style={{ margin: 0 }}>Trails in this org</h3>
        {trails.length === 0 ? (
          <div className="empty" style={{ marginTop: 12 }}>
            No trails linked yet.
          </div>
        ) : (
          <ul className="org-mgmt-list">
            {trails.map((t) => (
              <li key={t.slug}>
                <div className="org-mgmt-row" style={{ cursor: "default" }}>
                  <div className="org-mgmt-row-main">
                    <div className="org-mgmt-row-name">{t.name}</div>
                    <div className="org-mgmt-row-sub muted">
                      {[t.region, t.state].filter(Boolean).join(", ") || "No pin yet"}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KpiTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label muted">{label}</div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="kpi-hint muted">{hint}</div>}
    </div>
  );
}

function PendingKpi({ label, hint }: { label: string; hint: string }) {
  return (
    <div className="kpi-tile kpi-tile-pending">
      <div className="kpi-label muted">{label}</div>
      <div className="kpi-value kpi-value-pending">—</div>
      <div className="kpi-hint muted">{hint}</div>
      <div className="kpi-soon">Coming soon</div>
    </div>
  );
}

function humanDays(days: number): string {
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  const years = (days / 365).toFixed(1).replace(/\.0$/, "");
  return `${years}y`;
}
